'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import type { ImageType } from '@eliadmin/core/src/api/api-types';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from 'next/image';
import Link from '@eliadmin/core/src/components/link';
import React from 'react';
import { LuArrowLeft, LuX } from 'react-icons/lu';

export type Category = {
  data?: Category[];
  childs: Category[];
  name: string;
  description: string;
  short_description: string;
  slug: string;
  icon?: string;
  image?: ImageType;
};

type CategoriesPageNewProps = {
  categories: Category[];
};

export const CategoriesPageNew = ({ categories }: CategoriesPageNewProps) => {
  const { t } = useTranslations();

  return (
    <section className={cn('custom-section', 'pb-8 pt-4 sm:pb-10 sm:pt-5 lg:pb-12 lg:pt-8')}>
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-4 sm:gap-6 lg:gap-8',
        )}
      >
        <Heading title={t('text150')} tag="h1" />
        <div className="hidden w-full grid-cols-1 gap-2 sm:grid sm:grid-cols-2 md:gap-4 lg:grid-cols-3 xl:gap-6">
          {categories.map((category, index) => (
            <CategoryCard
              key={category.slug}
              category={category}
              isDesktop={true}
              eagerImage={index < 3}
            />
          ))}
        </div>
        <div className="grid w-full grid-cols-1 gap-2 sm:hidden">
          {categories.map((category, index) => (
            <CategoryCard
              key={category.slug}
              category={category}
              isDesktop={false}
              eagerImage={index < 3}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

function CategoryCard({
  category,
  isDesktop,
  eagerImage = false,
}: {
  category: Category;
  isDesktop: boolean;
  eagerImage?: boolean;
}) {
  if (isDesktop)
    return (
      <Link
        key={category.slug}
        href={ROUTES.CATEGORY({ slug: category?.slug })}
        prefetchOnHover
        className={cn(
          'relative col-span-1 flex h-[100px] w-full items-end justify-end overflow-hidden rounded-[4px] p-3 sm:h-[120px] sm:p-4 md:h-[140px] lg:h-[180px] lg:p-6',
        )}
      >
        <Heading
          color="white"
          tag="h3"
          as="h2"
          title={category?.name}
          className={{ container: 'relative z-10 w-full text-left' }}
        />
        <div className="ea-categories-card__image-overlay absolute inset-0 z-[1] transition-opacity duration-300 group-hover:opacity-0" />
        <div
          className="absolute inset-0 z-[1] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          style={{
            background: 'linear-gradient(250.53deg, #7B3F0C 0%, #7B3F0C 100%)',
            mixBlendMode: 'multiply',
          }}
        />
        <Image
          src={category?.image?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(category?.image, category?.name)}
          fill
          loading={eagerImage ? 'eager' : 'lazy'}
          className="absolute inset-0 z-0 h-full w-full object-cover object-center"
        />
      </Link>
    );

  return (
    <Sheet>
      <SheetTrigger className="relative col-span-1 flex h-[100px] w-full items-end justify-end overflow-hidden rounded-[4px] p-3 sm:h-[120px] sm:p-4 md:h-[140px] lg:h-[180px] lg:p-6">
        <Heading
          color="white"
          tag="h3"
          as="h2"
          title={category?.name}
          className={{ container: 'relative z-10 w-full text-left' }}
        />
        <div className="ea-categories-card__image-overlay absolute inset-0 z-[1] transition-opacity duration-300 group-hover:opacity-0" />
        <div
          className="absolute inset-0 z-[1] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          style={{
            background: 'linear-gradient(250.53deg, #7B3F0C 0%, #7B3F0C 100%)',
            mixBlendMode: 'multiply',
          }}
        />
        <Image
          src={category?.image?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(category?.image, category?.name)}
          fill
          loading={eagerImage ? 'eager' : 'lazy'}
          className="absolute inset-0 z-0 h-full w-full object-cover object-center"
        />
      </SheetTrigger>
      <SheetContent
        closeIcon={{ wrapper: 'hidden' }}
        className="hide-scroll-bar flex h-screen w-full max-w-[640px] flex-col items-start justify-start overflow-y-scroll p-0"
      >
        <div className="flex w-full items-center justify-end px-4 py-3.5">
          <SheetClose>
            <LuX size={24} className="text-black" />
          </SheetClose>
        </div>
        <div className="flex w-full flex-col items-start justify-start gap-4 p-4">
          <div className="flex items-center justify-start gap-2">
            <SheetClose>
              <LuArrowLeft size={24} className="text-primary" />
            </SheetClose>
            <SheetClose asChild>
              <Link href={ROUTES.CATEGORY({ slug: category?.slug })} prefetchOnHover>
                <Heading title={category?.name} />
              </Link>
            </SheetClose>
          </div>
          <div className="flex w-full flex-col items-start justify-start gap-2">
            {category.childs.map(child => (
              <SheetClose
                asChild
                key={child.slug}
                className="flex w-full items-center justify-start gap-2 rounded-[4px] border p-4"
              >
                <Link href={ROUTES.CATEGORY({ slug: child?.slug })} prefetchOnHover>
                  {/* <div className="relative max-h-12 min-h-12 min-w-12 max-w-12 rounded-[3px] bg-[#F4F4F4]">
                    {!!child?.icon?.url && (
                      <Image
                        src={child?.icon?.url}
                        alt={child?.name}
                        fill
                        className="object-contain object-center"
                      />
                    )}
                  </div> */}
                  <Heading tag="h3" className={{ title: 'font-heading' }} title={child?.name} />
                </Link>
              </SheetClose>
            ))}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
